/**
 * Mock 服务端：任务模板(JSON)、本地持久化、每日日切、execute/claim 校验。
 * 对齐 iOS TaskServerService；仅供 rewardService 调用。
 */
import rewardTaskRaw from '@/assets/local/reward_task.json'
import {
  TASK_TYPE,
  TASK_STATUS,
  REWARD_ERROR_CODE,
  RewardError
} from './rewardConstants'

export const STORAGE_STATES_KEY = 'appleshort_reward_task_states'
export const STORAGE_DAILY_RESET_DAY_KEY = 'appleshort_reward_daily_reset_day'

const MOCK_API_DELAY_MS = 1000

function cloneTask (task) {
  return task ? { ...task } : null
}

function cloneTaskList (list) {
  return (Array.isArray(list) ? list : []).map(cloneTask)
}

function isDailyTask (task) {
  return task?.task_type === TASK_TYPE.DAILY
}

export function localCalendarDayString () {
  const d = new Date()
  const y = d.getFullYear()
  const m = String(d.getMonth() + 1).padStart(2, '0')
  const day = String(d.getDate()).padStart(2, '0')
  return `${y}-${m}-${day}`
}

function taskFromDictionary (dictionary) {
  if (!dictionary || typeof dictionary !== 'object') {
    return null
  }

  return {
    task_id: dictionary.task_id || '',
    task_type: dictionary.task_type || '',
    status: Number.isFinite(Number(dictionary.status))
      ? Number(dictionary.status)
      : TASK_STATUS.UNFINISHED,
    amount: Number(dictionary.amount) || 0,
    progress: Number(dictionary.progress) || 0,
    total: Number(dictionary.total) > 0 ? Number(dictionary.total) : 1,
    jsonIndex: Number(dictionary.jsonIndex) || 0
  }
}

function taskToDictionary (task) {
  return {
    task_id: task.task_id || '',
    task_type: task.task_type || '',
    status: task.status,
    amount: task.amount,
    progress: task.progress,
    total: task.total
  }
}

function loadTemplateTasks () {
  return cloneTaskList(rewardTaskRaw).map((task, index) => ({
    ...task,
    jsonIndex: index
  }))
}

function readPersistedStates () {
  try {
    const raw = localStorage.getItem(STORAGE_STATES_KEY)
    if (!raw) return []
    const parsed = JSON.parse(raw)
    return Array.isArray(parsed) ? parsed : []
  } catch {
    return []
  }
}

function persistTaskList (tasks) {
  localStorage.setItem(
    STORAGE_STATES_KEY,
    JSON.stringify(tasks.map(taskToDictionary))
  )
  localStorage.setItem(STORAGE_DAILY_RESET_DAY_KEY, localCalendarDayString())
}

function deletePersistedDailyTaskStatesForDay (day) {
  const keptStates = readPersistedStates().filter((dictionary) => {
    if (!dictionary || typeof dictionary !== 'object') return false
    return dictionary.task_type !== TASK_TYPE.DAILY
  })

  localStorage.setItem(STORAGE_STATES_KEY, JSON.stringify(keptStates))
  localStorage.setItem(STORAGE_DAILY_RESET_DAY_KEY, day || localCalendarDayString())
}

function applyStateFromTask (target, source) {
  if (!target || !source) return
  target.status = source.status
  target.progress = source.progress
}

function mergeTaskListWithDailyReset (dailyDidReset) {
  const templateTasks = loadTemplateTasks()
  const persistedMap = new Map()

  for (const dictionary of readPersistedStates()) {
    const persistedTask = taskFromDictionary(dictionary)
    if (!persistedTask?.task_id) continue
    if (dailyDidReset && isDailyTask(persistedTask)) continue
    persistedMap.set(persistedTask.task_id, persistedTask)
  }

  return templateTasks.map((templateTask) => {
    const task = cloneTask(templateTask)
    const persisted = persistedMap.get(task.task_id)
    if (persisted) {
      applyStateFromTask(task, persisted)
    }
    return task
  })
}

function ensureTaskListSavingIfDailyReset () {
  const today = localCalendarDayString()
  const storedDay = localStorage.getItem(STORAGE_DAILY_RESET_DAY_KEY) || ''
  const dailyDidReset = !storedDay || storedDay !== today

  if (dailyDidReset) {
    deletePersistedDailyTaskStatesForDay(today)
  }

  return mergeTaskListWithDailyReset(false)
}

function findTaskWithId (tasks, taskId) {
  if (!taskId) return null
  return tasks.find((task) => task?.task_id === taskId) || null
}

function withDelay (resolver, delayMs = 0) {
  if (delayMs > 0) {
    return new Promise((resolve, reject) => {
      setTimeout(() => {
        try {
          resolve(resolver())
        } catch (e) {
          reject(e)
        }
      }, delayMs)
    })
  }
  return Promise.resolve().then(resolver)
}

export function mockGetRewardTasks (_uid) {
  return withDelay(() => ({
    tasks: cloneTaskList(ensureTaskListSavingIfDailyReset())
  }), MOCK_API_DELAY_MS)
}

export function mockExecuteTask (_uid, taskId, progressDelta = 1) {
  return withDelay(() => {
    const tasks = cloneTaskList(ensureTaskListSavingIfDailyReset())
    const task = findTaskWithId(tasks, taskId)

    if (!task) {
      throw new RewardError(REWARD_ERROR_CODE.TASK_NOT_FOUND, 'Task not found')
    }

    if (task.status !== TASK_STATUS.UNFINISHED) {
      return cloneTask(task)
    }

    const total = Number(task.total) > 0 ? Number(task.total) : 1
    const delta = Number(progressDelta) > 0 ? Number(progressDelta) : 1

    if (Number(task.progress) < total) {
      task.progress = Math.min(Number(task.progress) + delta, total)
    }

    if (Number(task.progress) >= total) {
      task.status = TASK_STATUS.CLAIMABLE
    }

    persistTaskList(tasks)
    return cloneTask(task)
  }, MOCK_API_DELAY_MS)
}

export function mockClaimRewardTask (_uid, taskId) {
  return withDelay(() => {
    const tasks = cloneTaskList(ensureTaskListSavingIfDailyReset())
    const task = findTaskWithId(tasks, taskId)

    if (!task) {
      throw new RewardError(REWARD_ERROR_CODE.TASK_NOT_FOUND, 'Task not found')
    }

    if (task.status === TASK_STATUS.UNFINISHED) {
      throw new RewardError(
        REWARD_ERROR_CODE.TASK_NOT_COMPLETED,
        'Task not completed'
      )
    }

    if (task.status === TASK_STATUS.CLAIMED) {
      throw new RewardError(
        REWARD_ERROR_CODE.TASK_REWARD_ALREADY_CLAIMED,
        'Reward already claimed'
      )
    }

    task.status = TASK_STATUS.CLAIMED
    persistTaskList(tasks)
    return cloneTask(task)
  }, MOCK_API_DELAY_MS)
}
